import type React from "react";

import {
    FlyoutTeaser,
    NavigationMenuItem,
} from "./navigationTypes";

interface NavigationRoot {
    children?: NavigationMenuItem[];
}

export const getNavigationRoot = (
    navigation: any
): NavigationRoot | undefined => {
    return navigation?.content?.site?.root;
};

export const hasChildren = (
    menuItem?: NavigationMenuItem | null
): boolean => {
    return Boolean(menuItem?.children?.length);
};

export const getItemLabel = (item: NavigationMenuItem): string => {
    return item.teaserTitle || item.name || "";
};

export const isExternalMenuLink = (
    item: NavigationMenuItem
): boolean => {
    return item.type === "CMExternalLink";
};

export const isOverviewItem = (
    menuItem: NavigationMenuItem
): boolean => {
    return String(menuItem.id ?? "").startsWith("overview-");
};

export const createOverviewItem = (
    parentItem: NavigationMenuItem | null | undefined,
    overviewLabel: string
): NavigationMenuItem | null => {
    if (!parentItem) {
        return null;
    }

    const navMenuTitle = parentItem.navMenuSettings?.navLinkTitle;
    const customTitle = typeof navMenuTitle === "string"
        ? navMenuTitle.trim()
        : "";

    return {
        ...parentItem,
        id: `overview-${parentItem.id}`,
        teaserTitle:
            customTitle ||
            `${overviewLabel} ${parentItem.teaserTitle || parentItem.name}`,
        children: [],
    };
};

export const withOverviewItem = (
    parentItem: NavigationMenuItem | null | undefined,
    overviewLabel: string
): NavigationMenuItem[] => {
    const items = [...(parentItem?.children ?? [])];
    const overviewItem = createOverviewItem(parentItem, overviewLabel);

    return overviewItem ? [overviewItem, ...items] : items;
};

export const getFlyoutHref = (
    teaser?: FlyoutTeaser | null
): string | undefined => {
    return teaser?.selfUrl || teaser?.target;
};

export const isMatchingUrl = (
    currentUrl?: string,
    itemUrl?: string
): boolean => {
    return Boolean(currentUrl && itemUrl && currentUrl.startsWith(itemUrl));
};

export const isMenuItemActive = (
    menuItem: NavigationMenuItem,
    currentUrl?: string
): boolean => {
    const itemUrl = menuItem.selfUrl;

    if (!currentUrl || !itemUrl) {
        return false;
    }

    return isOverviewItem(menuItem)
        ? currentUrl === itemUrl
        : currentUrl.startsWith(itemUrl);
};

export const isActiveThirdLevelParent = (
    secondLevelItem: NavigationMenuItem,
    currentUrl?: string
): boolean => {
    if (!hasChildren(secondLevelItem) || !currentUrl) {
        return false;
    }

    const hasActiveThirdLevelItem = secondLevelItem.children?.some((thirdLevelItem) =>
                                                                       isMatchingUrl(currentUrl, thirdLevelItem.selfUrl)
    );

    const isThirdLevelOverviewActive = currentUrl === secondLevelItem.selfUrl;

    return Boolean(hasActiveThirdLevelItem || isThirdLevelOverviewActive);
};

export const getSelectionWithSecondLevelItems = (
    selection: NavigationMenuItem | null | undefined,
    secondLevelItems: NavigationMenuItem[]
): { children: NavigationMenuItem[] } & NavigationMenuItem => {
    return {
        ...(selection ?? {}),
        children: secondLevelItems,
    };
};

export const isSameMenuItem = (
    firstItem: NavigationMenuItem | null,
    secondItem: NavigationMenuItem
): boolean => {
    if (!firstItem) {
        return false;
    }

    if (firstItem.id !== undefined && secondItem.id !== undefined) {
        return firstItem.id === secondItem.id;
    }

    return firstItem === secondItem;
};

export const isClickInsideRef = <T extends HTMLElement>(
    ref: React.RefObject<T | null>,
    target: Node
): boolean => {
    return ref.current instanceof Node && ref.current.contains(target);
};

export const isActivationKey = (
    event: React.KeyboardEvent
): boolean => {
    return event.key === "Enter" || event.key === " ";
};